"use strict";
/**
 * Market Switcher UI
 * The primary market list and prev/next navigation
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MARKET_ROW_ATTR = void 0;
exports.renderMarketSwitcher = renderMarketSwitcher;
exports.updateMarketSwitcher = updateMarketSwitcher;
exports.getSelectedMarketFromSwitcher = getSelectedMarketFromSwitcher;
exports.hideMarketSwitcher = hideMarketSwitcher;
exports.getAdjacentMarketId = getAdjacentMarketId;
exports.updateMarketNavButtons = updateMarketNavButtons;
exports.hideMarketNavButtons = hideMarketNavButtons;
const market_filter_1 = require("../markets/market-filter");
const market_label_1 = require("../markets/market-label");
/** A row in the primary market list; its market id rides on this attribute. */
exports.MARKET_ROW_ATTR = "data-market-id";
const SELECTED = "primary-market-item--selected";
/**
 * Render the primary market list: one tappable row per market the filter lets
 * through. The market being traded is always listed — pinned first and marked
 * when the filter hides it — because a filter change must never move the
 * operator to another market. Taps are handled by market-navigation.ts.
 */
function renderMarketSwitcher(markets, selectedMarketId, filter = market_filter_1.NO_FILTER) {
    const list = document.getElementById("market-switcher");
    if (!list)
        return;
    const shown = (0, market_filter_1.filterMarkets)(markets, filter);
    const selected = markets.find((m) => m.id === selectedMarketId);
    const hiddenSelected = selected && !shown.includes(selected) ? selected : null;
    const rows = hiddenSelected ? [hiddenSelected, ...shown] : shown;
    list.replaceChildren(...rows.map((market) => marketRow(market, market.id === selectedMarketId, market === hiddenSelected)));
    if (shown.length === 0) {
        const empty = document.createElement("p");
        empty.className = "parallel-markets-empty";
        empty.textContent = "No markets match the current filter.";
        list.appendChild(empty);
    }
    list.classList.toggle("hidden", markets.length < 2);
}
function marketRow(market, isSelected, filteredOut) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "primary-market-item";
    row.setAttribute(exports.MARKET_ROW_ATTR, market.id);
    row.setAttribute("role", "option");
    row.setAttribute("aria-selected", String(isSelected));
    row.classList.toggle(SELECTED, isSelected);
    const title = document.createElement("span");
    title.className = "parallel-markets-market-title";
    title.textContent = (0, market_label_1.marketLabel)(market);
    const meta = document.createElement("span");
    meta.className = "parallel-markets-market-meta";
    meta.textContent = [filteredOut ? "Filtered out" : "", market.subtitle].filter(Boolean).join(" · ");
    row.append(title, meta);
    return row;
}
/** Mark a market as the selected row. */
function updateMarketSwitcher(selectedMarketId) {
    const list = document.getElementById("market-switcher");
    if (!list)
        return;
    for (const row of list.querySelectorAll(`[${exports.MARKET_ROW_ATTR}]`)) {
        const isSelected = row.getAttribute(exports.MARKET_ROW_ATTR) === selectedMarketId;
        row.setAttribute("aria-selected", String(isSelected));
        row.classList.toggle(SELECTED, isSelected);
    }
}
/** The selected row's market id, or "" when none. */
function getSelectedMarketFromSwitcher() {
    const row = document
        .getElementById("market-switcher")
        ?.querySelector(`[${exports.MARKET_ROW_ATTR}][aria-selected="true"]`);
    return row?.getAttribute(exports.MARKET_ROW_ATTR) ?? "";
}
/**
 * Hide market switcher
 */
function hideMarketSwitcher() {
    const switcher = document.getElementById("market-switcher");
    if (!switcher)
        return;
    switcher.classList.add("hidden");
    switcher.innerHTML = "";
}
/**
 * Get the adjacent market ID in a circular list.
 * Returns null if markets array has fewer than 2 entries or selectedMarketId is not found.
 */
function getAdjacentMarketId(markets, selectedMarketId, direction) {
    if (markets.length < 2)
        return null;
    const currentIndex = markets.findIndex((m) => m.id === selectedMarketId);
    if (currentIndex === -1)
        return null;
    const offset = direction === "next" ? 1 : -1;
    const newIndex = (currentIndex + offset + markets.length) % markets.length;
    return markets[newIndex].id;
}
/**
 * Show or hide the prev/next market navigation buttons.
 * Buttons are visible only when more than one market is available.
 */
function updateMarketNavButtons(marketCount) {
    const show = marketCount > 1;
    for (const id of ["market-prev-btn", "market-next-btn"]) {
        const btn = document.getElementById(id);
        if (btn)
            btn.classList.toggle("hidden", !show);
    }
}
/**
 * Hide the prev/next market navigation buttons.
 */
function hideMarketNavButtons() {
    for (const id of ["market-prev-btn", "market-next-btn"]) {
        const btn = document.getElementById(id);
        if (btn)
            btn.classList.add("hidden");
    }
}
